import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useDealer } from "src/hooks/useDealer";
import Table from "src/components/Table";
import { Row, HeaderRow } from "src/components/Table/Table.styles";
import { HeaderCell } from "./Table/Cells/shared";
import { NameCell } from "src/components/Table/Cells/shared";
import { FileDOCIcon, FilePDFIcon } from "src/assets/icons";
import PaginationControls from "./PaginationControls";
import { useApiClient, useSafeApiCall } from "src/hooks/api";
import { API_PREFIX_PATH } from "src/constants";
import { PRIMARY_BLACK } from "src/constants/colors";
import { downloadDocument } from "src/utils";

const getFileType = (fileName: string): string =>
  fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2);

const getIconForFileType = (type: string): JSX.Element | null => {
  if (type.toLowerCase() === "pdf") {
    return <FilePDFIcon />;
  } else if (type.toLowerCase() === "doc" || type.toLowerCase() === "docx") {
    return <FileDOCIcon />;
  } else {
    return null;
  }
};

const FileCell = styled.div`
  display: flex;
  flex: 2;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  overflow: hidden;
`;

const FileTypeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  margin-left: -7px;
`;

const FileLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FileName = styled.div`
  color: ${PRIMARY_BLACK};
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const FileDate = styled.div`
  color: rgba(74, 84, 102, 1);
  font-size: 12px;
`;

interface DocumentItem {
  id: string;
  name: string;
  updated_at: string;
  uploaded_by_first_name: string;
  uploaded_by_last_name: string;
  uploaded_by_email: string;
}

interface DocumentsResponse {
  items: DocumentItem[];
  total: number;
}

interface DocumentsTableProps {
  data: {
    application_id: string;
  };
  allowPagination?: boolean;
}

export default ({ data, allowPagination = true }: DocumentsTableProps) => {
  const { dealer_id } = useDealer();
  const [documents, setDocuments] = useState<DocumentsResponse>({
    items: [],
    total: 0,
  });
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();
  const handleDownloadDocument = async (
    documentId: string,
    fileName: string,
  ) => {
    const path = `${API_PREFIX_PATH}/${dealer_id}/application-documents/${documentId}`;
    await downloadDocument(fetchFromApi, safeApiCall, path, fileName, false);
  };

  useEffect(() => {
    if (!dealer_id || !data.application_id) return;

    const path = `${API_PREFIX_PATH}/${dealer_id}/applications/${data.application_id}/documents`;
    const queryParams = {
      limit: pageSize,
      offset: offset,
    };

    void safeApiCall(() => fetchFromApi(path, { queryParams })).then((data) => {
      setDocuments(data as DocumentsResponse);
    });
  }, [data, offset, pageSize]);

  const Header = () => (
    <HeaderRow>
      <HeaderCell title="File name" />
      <HeaderCell title="Uploaded by" />
    </HeaderRow>
  );

  return (
    <>
      <Table
        Header={Header}
        rowBuilder={(obj) => {
          const {
            id,
            name,
            updated_at,
            uploaded_by_first_name,
            uploaded_by_last_name,
            uploaded_by_email,
          } = obj as unknown as DocumentItem;
          return (
            <Row
              key={id}
              onClick={() => {
                void handleDownloadDocument(id, name);
              }}
            >
              <FileCell>
                <FileTypeIcon>
                  {getIconForFileType(getFileType(name.toLowerCase()))}
                </FileTypeIcon>
                <FileLabelContainer>
                  <FileName>{name}</FileName>
                  <FileDate>{updated_at}</FileDate>
                </FileLabelContainer>
              </FileCell>
              <NameCell
                wide={false}
                firstName={uploaded_by_first_name}
                lastName={uploaded_by_last_name}
                email={uploaded_by_email}
              />
            </Row>
          );
        }}
        data={documents.items as unknown as Record<string, unknown>[]}
      />
      {allowPagination && (
        <PaginationControls
          offset={offset}
          setOffset={setOffset}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={documents.total}
        />
      )}
    </>
  );
};
